import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { ScrollText, Lock } from "lucide-react";
import { PageShell } from "../components/EmptyStateWrapper";
import { ShellContentFrame, ShellStatePane } from "../components/ShellContentFrame";

const ACTION_LABELS = {
    "user.registered": "User registered",
    "user.login": "Signed in",
    "org.created": "Workspace created",
    "pool.created": "Pool created",
    "pool.updated": "Pool updated",
    "pool.bank_updated": "Bank account linked",
    "invite.created": "Invitation sent",
    "invite.accepted": "Invitation accepted",
    "invite.revoked": "Invitation revoked",
    "percentage_change.requested": "Allocation change requested",
    "percentage_change.approved": "Allocation change approved",
    "percentage_change.applied_directly": "Allocations updated",
    "percentage_change.rejected": "Allocation change rejected",
    "payout_execution.requested": "Payout requested",
    "payout_execution.approved": "Payout approved",
    "payout_execution.applied_directly": "Payout executed",
    "payout_execution.rejected": "Payout rejected",
    "member_removal.requested": "Recipient removal requested",
    "member_removal.approved": "Recipient removed",
    "member_removal.applied_directly": "Recipient removed",
    "role_change.requested": "Role change requested",
    "role_change.approved": "Role changed",
    "schedule_change.requested": "Schedule change requested",
    "schedule_change.approved": "Schedule changed",
    "payment.checkout_created": "Stripe checkout created",
    "payment.revenue_credited": "Revenue credited",
    "pool.archived": "Pool archived",
    "pool.deleted": "Pool deleted",
};

function relTime(iso) {
    try {
        return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (_e) { return iso; }
}

function actionColor(action) {
    if (!action) return "text-white/55";
    if (action.endsWith("approved") || action.endsWith("applied_directly") || action === "payment.revenue_credited") return "text-[#00BF0D]";
    if (action.endsWith("rejected") || action === "invite.revoked") return "text-rose-400";
    if (action.endsWith("requested")) return "text-amber-300";
    return "text-white/75";
}

const AUDIT_HEADER = (
    <>
        <p className="label-eyebrow">Compliance</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-1">Audit log</h1>
        <p className="text-white/50 mt-1.5 max-w-2xl">Every state change is captured with actor, target, timestamp, and IP for traceable, audit-ready operations.</p>
    </>
);

export function AuditLogPage() {
    const { activeOrg } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [blocked, setBlocked] = useState(false);
    const [auditPolicy, setAuditPolicy] = useState(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!activeOrg?.id) {
                setLogs([]);
                setBlocked(false);
                setAuditPolicy(null);
                setLoading(false);
                return;
            }
            setLoading(true);
            setBlocked(false);
            try {
                const { data } = await api.get("/audit-logs", { params: { org_id: activeOrg.id, limit: 200 } });
                if (!mounted) return;
                setLogs(data.logs || []);
                setAuditPolicy(data.audit_log || null);
            } catch (e) {
                if (!mounted) return;
                if (e?.response?.status === 403) {
                    setBlocked(true);
                    setLogs([]);
                    setAuditPolicy(null);
                } else {
                    setBlocked(false);
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [activeOrg?.id]);

    const showLoadingReserve = loading;
    const showBlocked = !loading && blocked;
    const showEmpty = !loading && !blocked && logs.length === 0;
    const showList = !loading && !blocked && logs.length > 0;
    const weeklyCap = auditPolicy?.mode === "weekly";

    return (
        <PageShell>
            <div className="shrink-0 mb-6">{AUDIT_HEADER}</div>
            <ShellContentFrame
                loading={loading}
                loadingLabel="Loading audit log…"
                layout="stack"
                minHeight="min-h-[20rem]"
                className="flex-1"
                testId="audit-content-frame"
                loadingTestId="audit-loading"
            >
                <ShellStatePane active={showLoadingReserve} testId="audit-loading-reserve">
                    <div className="min-h-[20rem]" aria-hidden="true" />
                </ShellStatePane>

                <ShellStatePane active={showBlocked} testId="audit-blocked-pane">
                    <div className="absolute inset-0 grid place-items-center">
                        <div className="surface p-10 text-center w-full max-w-lg" data-testid="audit-upgrade-required">
                            <Lock className="h-6 w-6 text-white/35 mx-auto mb-2" />
                            <p className="text-sm font-medium text-white/85">Audit log is a paid feature</p>
                            <p className="text-xs text-white/45 mt-2 leading-relaxed">
                                Pro includes 50 events per rolling week. Business includes unlimited history.
                            </p>
                            <Link
                                to="/billing"
                                className="btn-neon inline-block mt-4 rounded-md px-4 py-2 text-sm"
                            >
                                View plans
                            </Link>
                        </div>
                    </div>
                </ShellStatePane>

                <ShellStatePane active={showEmpty} testId="audit-empty-pane">
                    <div className="absolute inset-0 grid place-items-center">
                        <div className="surface p-10 text-center w-full max-w-lg text-white/45" data-testid="audit-empty">
                            <ScrollText className="h-6 w-6 text-white/35 mx-auto mb-2" />
                            No activity yet.
                        </div>
                    </div>
                </ShellStatePane>

                <ShellStatePane active={showList} testId="audit-list-pane">
                    <div className="absolute inset-0 overflow-auto">
                        {weeklyCap && (
                            <p className="mb-3 text-[11px] text-amber-300/90 rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2">
                                Showing up to {auditPolicy?.max_per_week ?? 50} events from the last{" "}
                                {auditPolicy?.rolling_days ?? 7} days. Upgrade to Business for unlimited history.
                            </p>
                        )}
                        <div className="surface overflow-hidden">
                            <ul className="divide-y divide-white/[0.04]">
                                {logs.map((l) => (
                                    <li key={l.id} className="px-4 sm:px-5 py-3 flex items-start gap-3 hover:bg-white/[0.015] transition-colors" data-testid={`audit-${l.id}`}>
                                        <span className="mt-1.5 h-2 w-2 rounded-full bg-[#00BF0D]/70 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-sm font-medium ${actionColor(l.action)}`}>
                                                    {ACTION_LABELS[l.action] || l.action}
                                                </span>
                                                <span className="text-[11px] text-white/30">·</span>
                                                <span className="text-xs text-white/55">{l.user_name || l.user_email || "system"}</span>
                                            </div>
                                            {l.metadata && Object.keys(l.metadata).length > 0 && (
                                                <p className="text-[11px] text-white/35 font-mono mt-0.5 truncate">
                                                    {Object.entries(l.metadata).slice(0, 3).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v).slice(0, 40) : v}`).join(" · ")}
                                                </p>
                                            )}
                                        </div>
                                        <span className="text-[11px] text-white/35 font-mono shrink-0">{relTime(l.created_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </ShellStatePane>
            </ShellContentFrame>
        </PageShell>
    );
}
